import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Stack } from '@mui/material';
import { Button, EmptyState, TextLink, WarningCircleIcon } from '@natter-co/design-system';
import { Dialog } from 'common/components';
import { VISUAL_REFRESH } from 'common/utils';
import { WarningOutlinedIcon } from 'icons';

import { NavigationBar } from '../NavigationBar';

type Props = {
  title?: string;
  description?: string;
};

const HELP_CENTER_URL = 'https://intercom.help/natter/en/';

export const ErrorHandler = ({ title, description }: Props) => {
  const { t } = useTranslation('common');
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // A crash replaces the focused page; orient the user to the recovery screen once on entry.
    if (VISUAL_REFRESH) headingRef.current?.focus();
  }, []);

  if (VISUAL_REFRESH) {
    return (
      <div className="flex min-h-dvh flex-col bg-[var(--background-page)]">
        {/* The boundary does not reset on navigation, so a live logo link would change the URL and leave the crash screen up. */}
        <NavigationBar disableUserSection disableLogoInteraction />
        <main className="flex flex-1 items-center justify-center">
          <EmptyState
            className="max-w-2xl"
            size="page"
            headingLevel={1}
            headingRef={headingRef}
            icon={WarningCircleIcon}
            title={title ?? t('errorScreen.title')}
            description={
              <>
                {description ?? t('errorScreen.description')}
                <span className="mt-2 block">
                  <Trans
                    t={t}
                    i18nKey="errorScreen.help"
                    components={{
                      help: (
                        <TextLink href={HELP_CENTER_URL} external externalLabel={t('opensInNewTab')}>
                          {null}
                        </TextLink>
                      ),
                    }}
                  />
                </span>
              </>
            }
            actions=<Button size="large" onClick={() => window.location.reload()}>
              {t('errorScreen.refresh')}
            </Button>
          />
        </main>
      </div>
    );
  }

  return (
    <>
      <NavigationBar disableUserSection />
      <Dialog
        isOpen
        title={title ?? t('errorScreen.legacyTitle')}
        showCloseButton={false}
        showCancelButton={false}
        centered
      >
        <Stack alignItems="center" gap={2}>
          <WarningOutlinedIcon fontSize="large" color="error" />
          <div>{description ?? t('errorScreen.legacyDescription')}</div>
          <div>
            <Trans
              t={t}
              i18nKey="errorScreen.legacyHelp"
              components={{ help: <a href={HELP_CENTER_URL} target="_blank" rel="noreferrer" /> }}
            />
          </div>
        </Stack>
      </Dialog>
    </>
  );
};
